import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const LANGUAGE_KEY = 'lineside.voiceInput.language';
const recognitionConstructor = () => typeof window === 'undefined' ? null : window.SpeechRecognition || window.webkitSpeechRecognition;
const defaultLanguage = () => {
  try { return window.localStorage.getItem(LANGUAGE_KEY) || navigator.language || 'en-US'; }
  catch { return navigator.language || 'en-US'; }
};
const errorMessage = (code) => ({
  'not-allowed': 'Microphone access was denied. Allow it in your browser settings and try again.',
  'service-not-allowed': 'Speech recognition is blocked by your browser. You can still type your message.',
  'audio-capture': 'No microphone is available. Connect one and try again.',
  network: 'Speech recognition could not connect. Check your connection and try again.',
  'no-speech': 'No speech was heard. Try again when you are ready.',
  'language-not-supported': 'Your browser does not support this voice language. Choose another language or type your message.',
  aborted: 'Voice input stopped. You can try again or type your message.',
}[code] || 'Voice input could not start. Try again or type your message.');

/** Dictation edits a draft only after recognition ends; it never sends a message. */
export default function useVoiceInput({ message, setMessage, disabled = false, contextKey, maxMessageLength }) {
  const supported = Boolean(recognitionConstructor()) && window.isSecureContext !== false;
  const [language, setLanguageState] = useState(defaultLanguage);
  const [phase, setPhase] = useState('idle');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const sessionRef = useRef(null);
  const latest = useRef({ message, setMessage, disabled, contextKey, maxMessageLength });
  useLayoutEffect(() => { latest.current = { message, setMessage, disabled, contextKey, maxMessageLength }; });

  const release = useCallback((session) => {
    if (sessionRef.current !== session) return;
    sessionRef.current = null;
    clearTimeout(session.timer);
    session.recognition.onstart = null;
    session.recognition.onresult = null;
    session.recognition.onerror = null;
    session.recognition.onend = null;
    try { session.recognition.abort(); } catch { /* Already ended. */ }
  }, []);

  const cancel = useCallback(() => {
    if (sessionRef.current) release(sessionRef.current);
    setPhase('idle');
    setPreview('');
  }, [release]);

  // A new chat, switched agent, or a reply started elsewhere must not receive late speech.
  useLayoutEffect(() => { cancel(); setError(''); }, [contextKey, disabled, cancel]);
  useLayoutEffect(() => {
    if (sessionRef.current && message !== sessionRef.current.draft) cancel();
  }, [message, cancel]);
  useEffect(() => {
    const onHidden = () => { if (document.hidden) cancel(); };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', cancel);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', cancel);
      if (sessionRef.current) release(sessionRef.current);
    };
  }, [cancel, release]);

  const start = () => {
    if (!supported || latest.current.disabled || sessionRef.current) return;
    setError('');
    setPreview('');
    let recognition;
    try { recognition = new (recognitionConstructor())(); }
    catch { setError(errorMessage()); return; }
    const session = { recognition, contextKey, draft: message, finalText: '', timer: null, stopping: false };
    sessionRef.current = session;
    const isCurrent = () => sessionRef.current === session
      && latest.current.contextKey === session.contextKey && !latest.current.disabled;
    const finish = (failure = '') => {
      if (!isCurrent()) return;
      const text = session.finalText.trim();
      // External prefills own the composer if they replaced the draft while listening.
      if (text && latest.current.message === session.draft) {
        const separator = session.draft && !/\s$/.test(session.draft) ? ' ' : '';
        let next = session.draft + separator + text;
        const limit = latest.current.maxMessageLength;
        if (limit != null && next.length > limit) {
          next = next.slice(0, limit).replace(/[\uD800-\uDBFF]$/, '');
          failure = 'The message length limit was reached. Review the text before sending.';
        }
        latest.current.setMessage(next);
      }
      release(session);
      setPhase('idle');
      setPreview('');
      setError(failure || (!text ? errorMessage('no-speech') : ''));
    };
    session.stop = () => {
      if (!isCurrent() || session.stopping) return;
      session.stopping = true;
      setPhase('stopping');
      clearTimeout(session.timer);
      session.timer = setTimeout(() => finish('Voice input stopped before finishing. Review the text or try again.'), 3000);
      try { recognition.stop(); } catch { finish(errorMessage('aborted')); }
    };
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onstart = () => { if (isCurrent() && !session.stopping) setPhase('listening'); };
    recognition.onresult = (event) => {
      if (!isCurrent()) return;
      const final = [];
      const interim = [];
      // Results are cumulative; rebuilding avoids duplicate final segments.
      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        (result.isFinal ? final : interim).push(result[0].transcript.trim());
      }
      session.finalText = final.join(' ');
      setPreview([...final, ...interim].join(' '));
    };
    recognition.onerror = (event) => finish(errorMessage(event.error));
    recognition.onend = () => finish();
    setPhase('starting');
    // A bounded session also releases a stalled permission request or recognizer.
    session.timer = setTimeout(session.stop, 60000);
    try { recognition.start(); }
    catch (cause) { finish(errorMessage(cause.name === 'NotAllowedError' ? 'not-allowed' : undefined)); }
  };

  const setLanguage = (value) => {
    if (sessionRef.current) return;
    setLanguageState(value);
    setError('');
    try { window.localStorage.setItem(LANGUAGE_KEY, value); } catch { /* Optional preference. */ }
  };

  return {
    supported, phase, preview, error, language, setLanguage,
    active: phase !== 'idle',
    isActive: () => Boolean(sessionRef.current),
    start, stop: () => sessionRef.current?.stop(), cancel,
  };
}
